(() => {
  if (customElements.get('rr-scroll-glint')) return;

  class RRScrollGlint extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      if (window.Shopify?.theme?.role === 'main' || new URLSearchParams(location.search).get('rr_glint') === 'off') return;
      this.lifecycle = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.contrast = matchMedia('(forced-colors: active)');
      const options = { signal: this.lifecycle.signal };
      this.refresh = () => {
        this.stop();
        if (!this.motion.matches && !this.contrast.matches) this.start();
      };
      this.motion.addEventListener('change', this.refresh, options);
      this.contrast.addEventListener('change', this.refresh, options);
      document.addEventListener('shopify:section:load', this.refresh, options);
      document.addEventListener('shopify:section:unload', this.refresh, options);
      document.addEventListener('shopify:section:reorder', this.refresh, options);
      this.refresh();
    }

    start() {
      const main = document.querySelector('#main[data-rr-page-type="index"]');
      if (!main) return;
      this.items = [];
      this.decorations = [];
      this.labels = [];
      this.parents = new Map();
      this.registered = new WeakSet();
      this.byElement = new WeakMap();
      this.measureVersion = 0;
      this.active = new Set();
      this.frame = null;
      this.dirty = true;
      const add = (element, kind, pinned = false) => {
        // Keep homepage header text, logo and icons in their normal appearance.
        if (!element || element.closest('.header') || this.registered.has(element)) return;
        this.registered.add(element);
        element.removeAttribute('data-rr-glint-active');
        if (kind === 'detail') {
          // Loop copies can inherit our transparent ink; retain their original color.
          const base = element.style.getPropertyValue('--rr-glint-base') || getComputedStyle(element).color;
          element.style.setProperty('--rr-glint-base', base);
        }
        element.dataset.rrGlint = kind;
        const item = { element, start: 0, pinned };
        this.items.push(item);
        this.byElement.set(element, item);
        this.observer?.observe(element);
        if (kind === 'art' || kind === 'divider') {
          if (element instanceof SVGElement) {
            const ns = 'http://www.w3.org/2000/svg';
            const defs = document.createElementNS(ns, 'defs');
            const gradient = document.createElementNS(ns, 'linearGradient');
            gradient.id = `rr-glint-icon-${this.items.length}`;
            gradient.setAttribute('y1', '0%');
            gradient.setAttribute('y2', '30%');
            for (const offset of ['0%', '50%', '100%']) {
              const stop = document.createElementNS(ns, 'stop');
              stop.setAttribute('offset', offset);
              stop.setAttribute('stop-color', 'currentColor');
              gradient.append(stop);
            }
            defs.append(gradient);
            item.paints = [];
            for (const node of [element, ...element.querySelectorAll('[stroke="currentColor"], [fill="currentColor"]')]) {
              for (const name of ['stroke', 'fill']) if (node.getAttribute(name) === 'currentColor') {
                item.paints.push({ node, name });
                node.setAttribute(name, `url(#${gradient.id})`);
              }
            }
            element.append(defs);
            item.gradient = gradient;
            this.decorations.push(defs);
            return;
          }
          const parent = element.parentElement;
          if (!this.parents.has(parent) && getComputedStyle(parent).position === 'static') {
            this.parents.set(parent, parent.style.position);
            parent.style.position = 'relative';
          }
          const reflection = document.createElement('span');
          reflection.className = 'rr-glint-art-reflection';
          reflection.setAttribute('aria-hidden', 'true');
          if (kind === 'art') {
            const source = element instanceof HTMLImageElement ? element.currentSrc || element.src
              : `data:image/svg+xml,${encodeURIComponent(new XMLSerializer().serializeToString(element).replace(/currentColor/g, 'white'))}`;
            reflection.style.setProperty('--rr-glint-mask', `url(${JSON.stringify(source)})`);
          } else reflection.classList.add('rr-glint-divider-reflection');
          parent.append(reflection);
          item.reflection = reflection;
          this.decorations.push(reflection);
        }
        if (kind === 'button') {
          let label = element.querySelector('.rr-slideshow-button__label-rest');
          let wrapped = false;
          if (!label && element.children.length === 0) {
            label = document.createElement('span');
            while (element.firstChild) label.append(element.firstChild);
            element.append(label);
            wrapped = true;
          }
          if (label) {
            label.setAttribute('data-rr-glint-label', '');
            this.labels.push({ label, wrapped });
          }
          const reflection = document.createElement('span');
          reflection.className = 'rr-glint-reflection';
          reflection.setAttribute('aria-hidden', 'true');
          element.append(reflection);
          this.decorations.push(reflection);
        }
      };
      for (const slideshow of main.querySelectorAll('.shopify-section--slideshow')) {
        for (const heading of slideshow.querySelectorAll('.slideshow__slide-content .prose > :is(.h1, .h2, .h3, .h4, .h5, .h6)')) add(heading, 'text');
        for (const button of slideshow.querySelectorAll('.slideshow__slide-content .button')) add(button, 'button');
      }
      for (const collection of main.querySelectorAll('.shopify-section--featured-collections')) {
        for (const heading of collection.querySelectorAll('.section-stack > .v-stack > .h6')) {
          add(heading.querySelector('.rr-featured-collection-title') || heading, 'text');
        }
        for (const button of collection.querySelectorAll('.featured-collections-carousel__item > .justify-self-center > .button')) add(button, 'button');
      }
      const productSelector = 'product-card .product-title, product-card sale-price:not([data-rr-archived-label]), product-card compare-at-price';
      const navigationSelector = '.header__logo-text, .header__primary-nav a, .header__primary-nav summary, .header__secondary-nav > ul > li > a, .localization-toggle > span, #sidebar-menu a, #sidebar-menu button, .footer a';
      const within = (root, selector) => [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
      const scanDetails = (root = document) => {
        for (const element of within(root, productSelector)) add(element, 'detail');
        for (const element of within(root, navigationSelector)) {
          if (element.textContent.trim() && !element.querySelector('img, svg, input') && !element.closest('.rr-inline-collection__media')) add(element, 'detail', !!element.closest('.header, #sidebar-menu'));
        }
      };
      scanDetails();
      for (const element of document.querySelectorAll('.header__logo-image, .header .header__nav-icon, .header .rr-menu-close-icon')) add(element, 'art', true);
      for (const element of main.querySelectorAll('img')) {
        if (/\/(Diamond\.png|Wordmark\.svg)(?:\?|$)/i.test(element.src)) add(element, 'art');
      }
      for (const element of document.querySelectorAll('#main hr, #main [role="separator"], .footer hr, #sidebar-menu hr')) add(element, 'divider');
      this.events = new AbortController();
      const options = { passive: true, signal: this.events.signal };
      const schedule = () => {
        if (this.frame !== null || document.hidden || this.movingDialogs?.size) return;
        this.frame = requestAnimationFrame(() => this.paint());
      };
      const measure = () => { this.dirty = true; schedule(); };
      // Retain the current reflection during motion; resume scroll updates once settled.
      // Never remove enhancement styles or hide the glint when a drawer opens.
      this.movingDialogs = new Set();
      const beforeMotion = event => {
        this.movingDialogs.add(event.target);
        cancelAnimationFrame(this.frame);
        this.frame = null;
      };
      const afterMotion = event => {
        this.movingDialogs.delete(event.target);
        if (!this.movingDialogs.size) measure();
      };
      for (const type of ['dialog:before-show', 'dialog:before-hide']) {
        document.addEventListener(type, beforeMotion, { signal: this.events.signal, capture: true });
      }
      for (const type of ['dialog:after-show', 'dialog:after-hide']) {
        document.addEventListener(type, afterMotion, { signal: this.events.signal, capture: true });
      }
      this.changes = new MutationObserver(records => {
        const previousCount = this.items.length;
        if (records.some(record => record.removedNodes.length)) {
          this.items = this.items.filter(item => {
            if (item.element.isConnected) return true;
            this.observer.unobserve(item.element);
            this.registered.delete(item.element);
            this.byElement.delete(item.element);
            this.active.delete(item.element);
            return false;
          });
        }
        // Inspect only added subtrees, never rescan the full catalogue on scroll.
        for (const record of records) for (const node of record.addedNodes) {
          if (node.nodeType === 1 && !node.classList.contains('rr-glint-art-reflection')) scanDetails(node);
        }
        if (this.items.length !== previousCount) measure();
      });
      this.changes.observe(main, { childList: true, subtree: true });
      const menu = document.querySelector('#sidebar-menu');
      if (menu) this.changes.observe(menu, { childList: true, subtree: true });
      this.observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.active.add(entry.target);
            entry.target.setAttribute('data-rr-glint-active', '');
            const item = this.byElement.get(entry.target);
            if (item) item.measuredVersion = -1;
          }
          else {
            this.active.delete(entry.target);
            entry.target.removeAttribute('data-rr-glint-active');
          }
          const reflection = this.byElement.get(entry.target)?.reflection;
          if (reflection) reflection.hidden = !entry.isIntersecting;
        }
        schedule();
      }, { rootMargin: '100px 0px' });
      for (const item of this.items) this.observer.observe(item.element);
      this.resize = new ResizeObserver(measure);
      this.resize.observe(main);
      const header = document.querySelector('.header');
      if (header) this.resize.observe(header);
      main.addEventListener('load', measure, { signal: this.events.signal, capture: true });
      document.addEventListener('scroll', event => {
        if (event.target instanceof Element && event.target.closest('#sidebar-menu')) this.menuScroll = event.target.scrollTop;
        schedule();
      }, { ...options, capture: true });
      window.addEventListener('resize', measure, options);
      window.addEventListener('pageshow', measure, options);
      window.addEventListener('pagehide', () => {
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) { cancelAnimationFrame(this.frame); this.frame = null; }
        else measure();
      }, options);
      document.fonts?.addEventListener('loadingdone', measure, options);
      schedule();
    }

    paint() {
      this.frame = null;
      if (this.movingDialogs?.size) return;
      const y = window.scrollY;
      // Keep the pass visible until the target approaches the top of the viewport.
      const distance = Math.max(320, innerHeight * .85);
      if (this.dirty) {
        this.measureVersion++;
        this.dirty = false;
      }
      const visible = [...this.active].map(element => this.byElement.get(element)).filter(Boolean);
      // Finish every layout read before any style write. Offscreen copies stay unmeasured.
      for (const item of visible) {
        if (item.measuredVersion === this.measureVersion) continue;
        const { element, reflection } = item;
        const rect = element.getBoundingClientRect();
        item.start = Math.max(0, y + rect.top + rect.height / 2 - innerHeight * .92);
        if (reflection) {
          const parent = element.parentElement;
          const box = parent.getBoundingClientRect();
          item.geometry = {
            left: rect.left - box.left + parent.scrollLeft - parent.clientLeft,
            top: rect.top - box.top + parent.scrollTop - parent.clientTop,
            width: rect.width,
            height: element.dataset.rrGlint === 'divider' ? Math.max(1, rect.height) : rect.height
          };
        }
        item.measuredVersion = this.measureVersion;
      }
      for (const item of visible) {
        const { element, start, pinned, reflection, gradient } = item;
        if (item.geometry) {
          for (const [property, value] of Object.entries(item.geometry)) reflection.style[property] = `${value}px`;
          item.geometry = null;
        }
        const travel = pinned ? (y + (this.menuScroll || 0)) % (distance * 2) : y - start;
        const progress = Math.max(0, Math.min(1, travel / distance));
        const progressKey = progress.toFixed(4);
        if (item.progressKey === progressKey) continue;
        item.progressKey = progressKey;
        const presence = Math.sin(Math.PI * progress);
        element.style.setProperty('--rr-glint-position', `${100 - progress * 100}%`);
        element.style.setProperty('--rr-glint-presence', presence.toFixed(4));
        element.style.setProperty('--rr-glint-ink', (255 - presence * 72).toFixed(2));
        if (gradient) {
          gradient.setAttribute('x1', `${progress * 200 - 100}%`);
          gradient.setAttribute('x2', `${progress * 200}%`);
          gradient.firstElementChild.setAttribute('stop-opacity', String(1 - presence * .3));
          gradient.lastElementChild.setAttribute('stop-opacity', String(1 - presence * .3));
        }
        if (reflection) {
          reflection.style.setProperty('--rr-glint-position', `${100 - progress * 100}%`);
          reflection.style.opacity = String(presence * .38);
        }
      }
    }

    stop() {
      this.events?.abort();
      this.movingDialogs?.clear();
      this.observer?.disconnect();
      this.resize?.disconnect();
      this.changes?.disconnect();
      this.observer = null;
      this.resize = null;
      cancelAnimationFrame(this.frame);
      for (const { element, paints } of this.items || []) {
        for (const { node, name } of paints || []) node.setAttribute(name, 'currentColor');
        delete element.dataset.rrGlint;
        element.removeAttribute('data-rr-glint-active');
        for (const property of ['--rr-glint-position', '--rr-glint-presence', '--rr-glint-ink', '--rr-glint-base']) element.style.removeProperty(property);
      }
      for (const decoration of this.decorations || []) decoration.remove();
      for (const [parent, position] of this.parents || []) parent.style.position = position;
      for (const { label, wrapped } of this.labels || []) {
        if (wrapped) label.replaceWith(...label.childNodes);
        else label.removeAttribute('data-rr-glint-label');
      }
      this.items = [];
      this.decorations = [];
      this.labels = [];
      this.active?.clear();
    }

    disconnectedCallback() {
      this.stop();
      this.lifecycle?.abort();
      this.lifecycle = null;
    }
  }

  customElements.define('rr-scroll-glint', RRScrollGlint);
})();
